import { _decorator, Node, Label, Toggle, Vec3 } from 'cc';
import Dropdown from '../../../Components/Dropdown/Dropdown';
import ColorBalanceAdjuster, { ColorTone } from '../../../Components/Shader/ColorBalanceAdjuster';
import RangeSlider from '../../../Components/ValuePicker/RangeSlider';
import BaseColorAdjusterView, { IBaseColorAdjusterInfo } from './BaseColorAdjusterView';

interface IColorBalanceAdjusterInfo extends IBaseColorAdjusterInfo {
    shadow: { red: number; green: number; blue: number };
    midtone: { red: number; green: number; blue: number };
    highlight: { red: number; green: number; blue: number };
    keepLuminance: boolean;
}

const { ccclass, property } = _decorator;
const colorToneDisplayNameMap: { [key in ColorTone]: string } = {
    [ColorTone.Shadow]: '陰影',
    [ColorTone.Midtone]: '中間調',
    [ColorTone.Highlight]: '高光',
};

@ccclass('ColorBalanceAdjusterView')
export default class ColorBalanceAdjusterView extends BaseColorAdjusterView<ColorBalanceAdjuster> {
    @property(Dropdown)
    private readonly toneDropdown: Dropdown<ColorTone> = null;

    @property(RangeSlider)
    private readonly redBalance: RangeSlider = null;

    @property(RangeSlider)
    private readonly greenBalance: RangeSlider = null;

    @property(RangeSlider)
    private readonly blueBalance: RangeSlider = null;

    @property(Toggle)
    private readonly keepLuminance: Toggle = null;

    private _balances: Vec3[] = [];
    private _isSyncing: boolean = false;

    public onLoad() {
        super.onLoad();
        this._balances = Object.keys(ColorTone).map(() => new Vec3());
        this.toneDropdown.init(
            Object.values(ColorTone).filter((tone) => typeof tone === 'number') as ColorTone[],
            (displayName: Node, selectedOption: ColorTone) => {
                displayName.getComponent(Label).string = colorToneDisplayNameMap[selectedOption];
            },
            (target: Node, optionName: ColorTone) => {
                target.getComponentInChildren(Label).string = colorToneDisplayNameMap[optionName];
            },
        );
        this.toneDropdown.index = 1; // Default to Midtone
        this.toneDropdown.on(Dropdown.EVENT_TYPE.SELECTED_OPTION_CHANGED, this._optionChanged.bind(this));
        this.redBalance.on(RangeSlider.EVENT_TYPE.PROGRESS_CHANGED, this._redBalanceChanged.bind(this));
        this.greenBalance.on(RangeSlider.EVENT_TYPE.PROGRESS_CHANGED, this._greenBalanceChanged.bind(this));
        this.blueBalance.on(RangeSlider.EVENT_TYPE.PROGRESS_CHANGED, this._blueBalanceChanged.bind(this));
        this.keepLuminance.on(Toggle.EventType.TOGGLE, this.apply.bind(this));
    }

    public collectAdjustInfo(): IColorBalanceAdjusterInfo {
        const target = this.currentTarget;
        if (!target) {
            return null;
        }
        return {
            name: 'colorBalance',
            shadow: {
                red: target.shadowRedBalance,
                green: target.shadowGreenBalance,
                blue: target.shadowBlueBalance,
            },
            midtone: {
                red: target.midtoneRedBalance,
                green: target.midtoneGreenBalance,
                blue: target.midtoneBlueBalance,
            },
            highlight: {
                red: target.highlightRedBalance,
                green: target.highlightGreenBalance,
                blue: target.highlightBlueBalance,
            },
            keepLuminance: target.keepLuminance,
        };
    }

    protected apply() {
        if (this._isSyncing) {
            return;
        }
        const target = this.currentTarget;
        if (!target) {
            return;
        }
        target.shadowRedBalance = this._balances[ColorTone.Shadow].x;
        target.shadowGreenBalance = this._balances[ColorTone.Shadow].y;
        target.shadowBlueBalance = this._balances[ColorTone.Shadow].z;

        target.midtoneRedBalance = this._balances[ColorTone.Midtone].x;
        target.midtoneGreenBalance = this._balances[ColorTone.Midtone].y;
        target.midtoneBlueBalance = this._balances[ColorTone.Midtone].z;

        target.highlightRedBalance = this._balances[ColorTone.Highlight].x;
        target.highlightGreenBalance = this._balances[ColorTone.Highlight].y;
        target.highlightBlueBalance = this._balances[ColorTone.Highlight].z;

        target.keepLuminance = this.keepLuminance.isChecked;
    }

    protected syncUIWithTarget() {
        const target = this.currentTarget;
        if (!target) {
            return;
        }
        this._isSyncing = true;
        this.redBalance.progress = (target.shadowRedBalance - this.redBalance.min) / (this.redBalance.max - this.redBalance.min);
        this.greenBalance.progress = (target.shadowGreenBalance - this.greenBalance.min) / (this.greenBalance.max - this.greenBalance.min);
        this.blueBalance.progress = (target.shadowBlueBalance - this.blueBalance.min) / (this.blueBalance.max - this.blueBalance.min);
        this.keepLuminance.isChecked = target.keepLuminance;
        this._balances[ColorTone.Shadow] = new Vec3(target.shadowRedBalance, target.shadowGreenBalance, target.shadowBlueBalance);
        this._balances[ColorTone.Midtone] = new Vec3(target.midtoneRedBalance, target.midtoneGreenBalance, target.midtoneBlueBalance);
        this._balances[ColorTone.Highlight] = new Vec3(target.highlightRedBalance, target.highlightGreenBalance, target.highlightBlueBalance);
        this.toneDropdown.index = ColorTone.Midtone; // Default to Midtone
        this._isSyncing = false;
    }

    protected reset() {
        this.redBalance.progress = 0.5;
        this.greenBalance.progress = 0.5;
        this.blueBalance.progress = 0.5;
        this.keepLuminance.isChecked = true;
        this._balances[ColorTone.Shadow] = new Vec3(0, 0, 0);
        this._balances[ColorTone.Midtone] = new Vec3(0, 0, 0);
        this._balances[ColorTone.Highlight] = new Vec3(0, 0, 0);
        this.apply();
    }

    private _redBalanceChanged() {
        const { index } = this.toneDropdown;
        this._balances[index].x = this.redBalance.value;
        this.apply();
    }

    private _greenBalanceChanged() {
        const { index } = this.toneDropdown;
        this._balances[index].y = this.greenBalance.value;
        this.apply();
    }

    private _blueBalanceChanged() {
        const { index } = this.toneDropdown;
        this._balances[index].z = this.blueBalance.value;
        this.apply();
    }

    private _optionChanged() {
        const { index } = this.toneDropdown;
        const balance = this._balances[index];
        this.redBalance.progress = (balance.x - this.redBalance.min) / (this.redBalance.max - this.redBalance.min);
        this.greenBalance.progress = (balance.y - this.greenBalance.min) / (this.greenBalance.max - this.greenBalance.min);
        this.blueBalance.progress = (balance.z - this.blueBalance.min) / (this.blueBalance.max - this.blueBalance.min);
        this.apply();
    }
}
